import React, { useEffect, useState, useMemo } from 'react';
import { useRecoilState, useRecoilValueLoadable } from 'recoil';
import SearchableDropdown from '../../FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';
import useDebounce from '../Delay/index.tsx';
// Assuming generateFilterKey is correctly imported from utils/helper
// If it's a .jsx file exporting a function, ensure it works as expected in TS.
// Consider renaming to .ts or .tsx if it contains React/TS logic.
import { generateFilterKey } from '../../../utils/helper.jsx';
import { entityFormsStateAtomFamily, entityTableStateAtom } from '../../../Services/_states/index.js';
import { fetchEntityFormListSelector } from '../../../Services/_states/Entity/entitySelector.ts';

interface EntityDropdownProps {
  value: any; // Consider using a more specific type if possible, e.g., string | null
  onChange: (value: any) => void; // Consider using a more specific type if possible
  error?: boolean;
  errorMessage?: string;
  label?: any;
  disabled?: boolean;
  sx?: any;
  organisation_id?: string;
  getOptionDisabled?: (option: any) => boolean;
  uniqueId?: string;
}
// Define a more specific type for your entity if possible
interface Entity {
  _id: string;
  name: string;
  // Add other properties of your entity object
}

// Define a more specific type for the table state atom's value
interface EntityTableState {
  filters: {
    search: string;
    page: number;
    // Add other filter properties if they exist
  };
  cache: { [key: string]: Entity[] }; // Cache stores arrays of entities
  totalCountCache: { [key: string]: number };
  // Add other state properties if they exist
}

const EntityDropdown: React.FC<EntityDropdownProps> = ({
  value,
  onChange,
  error = false,
  errorMessage = '',
  label = 'Select Entity',
  disabled = false,
  sx = { mb: 1, mt: 1 },
  organisation_id = null,
  getOptionDisabled,
  uniqueId = "uniqueId", // Default uniqueId if not provided
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 500);
  const [tableState, setTableState] = useRecoilState(entityFormsStateAtomFamily(uniqueId));
  const fetchedData = useRecoilValueLoadable(fetchEntityFormListSelector(uniqueId));
  const [openDropdown, setOpenDropdown] = useState(false);

  // --- State Synchronization Effects ---
  useEffect(() => {
    if (!openDropdown) return;
    if (debouncedSearch !== tableState.filters.search) {
      setTableState((prev) => ({
        ...prev,
        filters: { ...prev.filters, search: debouncedSearch, page: 0 }, // Reset page on new search
      }));
    }
  }, [debouncedSearch, setTableState]);

  useEffect(() => {
    if (!openDropdown) return;
    setTableState((prev) => {
      let updatedFilters = { ...prev.filters };
      let changed = false;
      if (organisation_id && prev.filters.organisation_id !== organisation_id) {
        updatedFilters.organisation_id = organisation_id;
        changed = true;
      }
      return changed ? { ...prev, filters: updatedFilters } : prev;
    });
  }, [organisation_id, setTableState, openDropdown]);
  // --- Cache Management Effect ---

  // Update cache when new data is fetched successfully
  useEffect(() => {
    if (fetchedData.state === 'hasValue' && fetchedData.contents) {
      const filterKeyForFetchedData = generateFilterKey(tableState.filters);
      const cacheExists = !!tableState.cache[filterKeyForFetchedData];
      const totalCountMatches = tableState.totalCountCache[filterKeyForFetchedData] === fetchedData.contents.totalCount;
      if (!cacheExists || !totalCountMatches) {
        setTableState((prev) => {
          // if (prev?.filters?.parentEntityFormId) {
          //   return {
          //     ...prev,
          //     cache: { [filterKeyForFetchedData]: fetchedData.contents.data },
          //     totalCountCache: { [filterKeyForFetchedData]: fetchedData.contents.totalCount },
          //   };
          // }
          if (prev.cache[filterKeyForFetchedData] && prev.totalCountCache[filterKeyForFetchedData] === fetchedData.contents.totalCount) {
            return prev; // Return previous state if somehow it got updated between the outer check and here
          }

          const newCache = { ...prev.cache };
          const newTotalCountCache = { ...prev.totalCountCache };

          newCache[filterKeyForFetchedData] = fetchedData.contents.data;
          newTotalCountCache[filterKeyForFetchedData] = fetchedData.contents.totalCount;

          return {
            ...prev,
            cache: newCache,
            totalCountCache: newTotalCountCache,
          };
        });
      }
    }
  }, [
    fetchedData,
    setTableState,
    tableState.filters,
    tableState.cache, // Added dependency
    tableState.totalCountCache // Added dependency
  ]);

  // --- Derived State for Dropdown Options ---

  // Memoize the calculation of dropdown options based on the *current* search and cache
  const optionsForDropdown = useMemo(() => {
    const relevantEntities: Entity[] = []; // Use Entity type
    const seenIds = new Set < string > ();

    // Iterate through pages fetched for the *current* search term and filters
    for (let i = 0; i <= tableState.filters.page; i++) {
      // Generate key based on current filters but varying page number
      const pageFilterKey = generateFilterKey({ ...tableState.filters, page: i });
      const pageData = tableState.cache[pageFilterKey];

      if (pageData) {
        pageData.forEach((entity: Entity) => { // Use Entity type
          // Ensure uniqueness within the options list for the current search
          if (entity?._id && !seenIds.has(entity._id)) {
            relevantEntities.push(entity);
            seenIds.add(entity._id);
          }
        });
      }
    }
    return relevantEntities;
  }, [tableState.cache, tableState.filters]);

  // --- Load More Logic ---

  const loadMoreEntities = () => {
    const currentFilterKey = generateFilterKey(tableState.filters);
    const total = tableState.totalCountCache[currentFilterKey] ?? 0; // Get total count for this search/filter set
    const currentLength = optionsForDropdown.length;
    if (total > currentLength) {
      setTableState((prev) => ({
        ...prev,
        filters: { ...prev.filters, page: prev.filters.page + 1 }, // Increment page number
      }));
    } else {
      console.log("Load More: No more items to load (total:", total, ", current:", currentLength, ") or total count unknown.");
    }
  };

  return (
    <SearchableDropdown
      options={optionsForDropdown} // Use the derived options
      label={label}
      value={value || null}
      labelKey="name" // Make sure your Entity type has a 'name' property
      valueKey="_id"  // Make sure your Entity type has an '_id' property
      onChange={onChange}
      onSearch={setSearchQuery}
      fetchMoreOptions={loadMoreEntities}
      // Show loading indicator if the selector is loading OR if we are loading the next page but haven't received data yet
      loading={fetchedData.state === 'loading'}
      error={error} // Pass down external error state
      errorMessage={errorMessage} // Pass down external error message
      disabled={disabled}
      sx={sx}
      searchValue={searchQuery} // Control the search input value
      getOptionDisabled={getOptionDisabled}
      onOpen={() => {
        setOpenDropdown(true);
      }}
      onClose={() => {
        setOpenDropdown(false);
      }}

    />
  );
};

export default EntityDropdown;